/**
 * What each buyer plan includes — the single source of truth.
 *
 * The choose-plan page (plan cards + comparison table), the signup plan picker
 * (TierSelector) and the upgrade / benefits dialogs on the subscription page
 * all render from here, so the same plan can never be described two different
 * ways depending on where the buyer is standing.
 *
 * Columns are positional: 0 = Explorer, 1 = Operator. Plans are matched to a
 * column BY NAME, never by tier id — ids are SERIAL and differ per environment
 * (Explorer is id 5 in dev), and the retired "Tier 1-4" rows hold the low ids.
 */

export type PlanCellValue = boolean | string;

export const PLAN_NAMES = ["Explorer", "Operator"] as const;

const PLAN_COLUMN_BY_NAME: Record<string, number> = {
  explorer: 0,
  operator: 1,
};

/**
 * Matrix column for a plan, or `fallback` when the name isn't one of the
 * current plans (a grandfathered "Tier 1"-style subscription).
 */
export function tierColumnIndex(tierName: string | null | undefined, fallback = -1): number {
  const key = tierName?.trim().toLowerCase() ?? "";
  const col = PLAN_COLUMN_BY_NAME[key];
  return col === undefined ? fallback : col;
}

/** Plan-card bullets, indexed by column. */
export const PLAN_FEATURES: string[][] = [
  // Explorer
  [
    "Everything in Basic",
    "Coming Soon deals",
    "Advanced filters",
    "Deal map view",
    "18 listing unlocks / month",
    "Max 5 deal interest / day",
    "Full financials, notes & growth thesis",
    "Basic AI deal match",
    "BL Confidence Scoring",
    "Saved searches",
    "BuyBox deal alerts",
    "Favorite your deals",
    "5-year financial model with IRR scenarios",
    "Niva AI Deal Assistant — free for 1 week",
  ],
  // Operator
  [
    "Unlimited listing unlocks",
    "Early deal locks — 24-hr first look",
    "Unlimited deal interest / messages",
    "Advanced AI voice filter",
    "Map and grid views for deals",
    "Priority AI deal matching",
    "Full BL Confidence Scoring",
    "AI risk flags with detailed insights",
    "Deal pipeline CRM (10 deals)",
    "Priority concierge intros",
    "Niva AI Deal Assistant",
    "AI NDA + LOI generator (Coming Soon)",
    "Virtual Data Room (Coming Soon)",
    "Deal valuation comps (Coming Soon)",
  ],
];

/** The full, grouped feature comparison shown on the choose-plan page. */
export const PLAN_COMPARE_GROUPS: { group: string; rows: [string, PlanCellValue, PlanCellValue][] }[] = [
  {
    group: "Sourcing & unlocks",
    rows: [
      // Basic (no subscription) earns 2 unlocks per week, carried over to month end — see BASIC_FREE_UNLOCKS
      // in app/services/tier.py; keep the number here in step with it.
      ["Monthly listing unlocks", "18 / month", "Unlimited"],
      ["Additional unlocks", "$10 each", "Included"],
      ["Coming Soon deals", true, true],
      ["Deal interest / messages", "Max 5 / day", "Unlimited"],
      ["Early deal locks", false, "24-hr first look"],
    ],
  },
  {
    group: "Search & discovery",
    rows: [
      ["Advanced filters", true, true],
      ["Advanced AI voice filter", false, true],
      ["Deal views", "Map view", "Map + grid views"],
      ["Saved searches", true, true],
      ["BuyBox deal alerts", true, true],
      ["Favorite your deals", true, true],
    ],
  },
  {
    group: "AI deal intelligence",
    rows: [
      ["Full financials, notes & growth thesis", true, true],
      ["AI deal matching", "Basic", "Priority"],
      ["BL Confidence Scoring", "Included", "Full"],
      ["AI risk flags with detailed insights", false, true],
      ["Niva AI Deal Assistant", "Free for 1 week", true],
    ],
  },
  {
    group: "Deal management & support",
    rows: [
      // Live for every paid plan (the route is MembersOnlyGate, not requires="operator").
      ["5-year financial model & IRR scenarios", true, true],
      ["Deal pipeline CRM", false, "10 deals"],
      ["Concierge intros", false, "Priority"],
      ["Success fee", "At closing", "At closing"],
    ],
  },
  {
    group: "Coming soon to Operator",
    rows: [
      ["AI NDA + LOI generator", false, "Coming Soon"],
      ["Virtual Data Room", false, "Coming Soon"],
      ["Deal valuation comps", false, "Coming Soon"],
    ],
  },
];

// Headline rows for the compact signup picker table. Values are looked up in
// PLAN_COMPARE_GROUPS rather than restated, so the short table can never claim
// something the full comparison contradicts.
const COMPACT_ROW_LABELS = [
  "Monthly listing unlocks",
  "Additional unlocks",
  "Deal interest / messages",
  "Advanced filters",
  "AI deal matching",
  "Early deal locks",
  "Deal pipeline CRM",
  "Concierge intros",
  "Niva AI Deal Assistant",
];

const ALL_COMPARE_ROWS = PLAN_COMPARE_GROUPS.flatMap((section) => section.rows);

export const PLAN_COMPARE_COMPACT: { feature: string; values: PlanCellValue[] }[] = COMPACT_ROW_LABELS
  .map((label) => ALL_COMPARE_ROWS.find((row) => row[0] === label))
  .filter((row): row is [string, PlanCellValue, PlanCellValue] => row !== undefined)
  .map(([feature, explorer, operator]) => ({ feature, values: [explorer, operator] }));

/**
 * One plan's card bullets. Empty for a plan that isn't in the matrix — callers
 * fall back to that tier's own `features_text`.
 */
export function planFeatures(tierName: string | null | undefined): string[] {
  const col = tierColumnIndex(tierName);
  return col < 0 ? [] : PLAN_FEATURES[col] ?? [];
}
